import {
  Game,
  getApiPath,
  getPlannerUrl,
  getStoredValue,
  removedStoredValue
} from 'common'
import React from 'react'
import Session from 'store/session'

const tokenVar = 'd2p-token'

export class Api {
  constructor(store) {
    this.store = store
    this.game = Game
  }

  async updateLoginFrontend() {
    const url = new URL(getApiPath(`users/whoami`), window.location.href)
    const result = await this.fetchAuth(url, {
      method: 'GET'
    })
    if (result && result.userId) {
      this.store?.dispatch.setUser(
        result.userId,
        result.userName,
        result.userLevel || 1
      )
    }
    return result.userId
  }

  authHeaders() {
    const token = getStoredValue(tokenVar, () => getStoredValue(tokenVar), true)
    if (token) {
      return {
        Authorization: `Bearer ${token}`
      }
    }
  }

  signout() {
    removedStoredValue(tokenVar)
    removedStoredValue(tokenVar, true)
    if (this.store) {
      this.store.dispatch.setUser(0, '', 0)
    }
  }

  async fetchAuth(url, options) {
    const response = await fetch(url.toString(), {
      ...options,
      credentials: 'include',
      headers: { ...options.headers, ...this.authHeaders() }
    }).catch(() => null)
    if (!response) {
      return { error: 'No response.' }
    }
    if (response.status === 401) {
      this.signout()
      return { error: `Unauthorized.` }
    }
    const result = await response.json().catch(() => null)
    if (!response?.ok) {
      return { error: result?.error || `Unknown error.` }
    }
    return result
  }

  async save(data, params) {
    const url = new URL(getApiPath(`profiles/${this.game}`), window.location)
    if (params) url.search = new URLSearchParams(params).toString()

    const result = await this.fetchAuth(url, {
      method: 'POST',
      body: JSON.stringify(data),
      headers: { 'Content-Type': 'application/json' }
    })

    if (!result || !result.id) {
      throw Error(result?.error || `Failed to save profile.`)
    }

    return result.id
  }

  async load(id) {
    const result = await fetch(getApiPath(`profiles/${this.game}/${id}`), {
      method: 'GET'
    })
      .then((response) => response.json())
      .catch(() => null)

    if (!result || result.error || !result.data) {
      throw Error(result?.error || `Unknown error`)
    }

    return JSON.parse(result.data)
  }

  async delete(id) {
    const result = await this.fetchAuth(getApiPath(`profiles/${id}`), {
      method: 'DELETE'
    })

    if (!result || result.code !== 'OK') {
      throw Error(result?.error || `Failed to delete profile.`)
    }

    return true
  }

  async search(params) {
    const url = new URL(
      getApiPath(`search/legacy/${this.game}`),
      window.location
    )
    if (params) url.search = new URLSearchParams(params).toString()

    const result = await this.fetchAuth(url, {
      method: 'GET'
    })

    if (!result || !Array.isArray(result)) {
      throw Error(result?.error || `Query failed.`)
    }

    return result.map((row) => {
      if (typeof row.date === 'string') {
        row = { ...row, date: (Date.parse(row.date) / 1000) | 0 }
      }
      return row
    })
  }

  async folders(action, params) {
    if (action === 'save') {
      const result = await this.fetchAuth(getApiPath(`folders/${this.game}`), {
        method: 'POST',
        body: JSON.stringify(params),
        headers: { 'Content-Type': 'application/json' }
      })
      if (!result || result.code !== 'OK') {
        throw Error(result?.error || `Failed to modify folders.`)
      }
      return true
    } else if (action === 'get' || !action) {
      const result = await this.fetchAuth(getApiPath(`folders/${this.game}`), {
        method: 'GET'
      })
      if (!result || result.error) {
        throw Error(result?.error || `Failed to load folders.`)
      }
      return result
    } else if (action === 'delall') {
      const result = await this.fetchAuth(
        getApiPath(`profiles/by-folder/${params.from}`),
        {
          method: 'DELETE'
        }
      )
      if (!result || result.code !== 'OK') {
        throw Error(result?.error || `Failed to modify folders.`)
      }
      return true
    } else if (action === 'moveall') {
      const result = await this.fetchAuth(getApiPath(`profiles/move`), {
        method: 'POST',
        body: JSON.stringify({ from: params.from, to: params.to }),
        headers: { 'Content-Type': 'application/json' }
      })
      if (!result || result.code !== 'OK') {
        throw Error(result?.error || `Failed to modify folders.`)
      }
      return true
    } else if (action === 'move') {
      const result = await this.fetchAuth(getApiPath(`profiles/move`), {
        method: 'POST',
        body: JSON.stringify({ id: params.id, to: params.to }),
        headers: { 'Content-Type': 'application/json' }
      })
      if (!result || result.code !== 'OK') {
        throw Error(result?.error || `Failed to modify folders.`)
      }
      return true
    } else if (action === 'rename') {
      const result = await this.fetchAuth(getApiPath(`profiles/rename`), {
        method: 'POST',
        body: JSON.stringify({ id: params.id, name: params.name }),
        headers: { 'Content-Type': 'application/json' }
      })
      if (!result || result.code !== 'OK') {
        throw Error(result?.error || `Failed to modify folders.`)
      }
      return true
    } else {
      throw Error('Unknown action.')
    }
  }

  async grail(action, params, body) {
    const plannerUrl = getPlannerUrl()
    const basePath = plannerUrl + '/grail'

    if (body) body = JSON.stringify(body)

    let result = null

    if (action === 'saveProfile') {
      const profileId = params.id
      const requestUrl = basePath + `/${profileId}`
      result = await this.fetchAuth(requestUrl, { method: 'PATCH', body })
    }

    if (action === 'loadUser') {
      result = await this.fetchAuth(basePath, { method: 'GET' })
    }

    if (action === 'loadProfile') {
      const profileId = params.id
      const requestUrl = basePath + `/${profileId}`
      result = await this.fetchAuth(requestUrl, { method: 'GET' })
    }

    if (action === 'createProfile') {
      result = await this.fetchAuth(basePath, { method: 'POST', body })
    }

    if (action === 'deleteProfile') {
      const profileId = params.id
      const requestUrl = basePath + `/${profileId}`
      result = await this.fetchAuth(requestUrl, { method: 'DELETE' })
    }

    if (action === 'createInvite') {
      const profileId = params.id
      const requestUrl = basePath + `/${profileId}/invite`
      result = await this.fetchAuth(requestUrl, { method: 'POST' })
    }

    if (action === 'removeUser') {
      const profileId = params.id
      const user = params.user
      const requestUrl = basePath + `/${profileId}/user?user=${user}`
      result = await this.fetchAuth(requestUrl, { method: 'DELETE' })
    }

    if (action === 'promoteUser') {
      const profileId = params.id
      const user = params.user
      const requestUrl = basePath + `/${profileId}/user/promote?user=${user}`
      result = await this.fetchAuth(requestUrl, { method: 'POST' })
    }

    if (action === 'acceptInvite') {
      const profileId = params.id
      const invite = params.invite
      const requestUrl =
        basePath + `/${profileId}/invite/accept?invite=${invite}`
      result = await this.fetchAuth(requestUrl, { method: 'POST', body })
    }

    if (!result || result.error) {
      throw Error(result?.error || `Unknown error.`)
    }

    return result
  }
}

export default function useApi() {
  const store = Session.useStore()
  return React.useMemo(() => new Api(store), [store])
}
